import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
    Schedule,
    BarChart as BarChartIcon,
    CalendarToday,
    Category,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { ReportChart } from './ReportChart';
import { formatDateForInput } from '../../utils/dateUtils';

// ---- Types ----------------------------------------------------------------

interface PeriodData {
    availableMinutes: number;
    amenityBookingMinutes: number;
    coachingMinutes: number;
    groupPlayMinutes: number;
    maintenanceMinutes: number;
    holdsMinutes: number;
    totalBookedMinutes: number;
    utilizationRate: number;
}

interface AmenityUtilization {
    id: string;
    name: string;
    type: string;
    periods: Record<string, PeriodData>;
    totalAvailableMinutes: number;
    totalBookedMinutes: number;
    utilizationRate: number;
}

interface CourtUtilizationData {
    periods: string[];
    periodKeys: string[];
    amenities: AmenityUtilization[];
    summary: {
        totalAvailableHours: number;
        totalBookedHours: number;
        overallUtilizationRate: number;
        amenityBookingHours: number;
        coachingHours: number;
        groupPlayHours: number;
        maintenanceHours: number;
        holdsHours: number;
        byPeriod: Record<string, { availableHours: number; bookedHours: number; utilizationRate: number }>;
    };
    amenityTypes: string[];
}

// ---- Helpers --------------------------------------------------------------

const fmtHours = (hours: number) =>
    `${hours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`;
const fmtMinH = (minutes: number) =>
    `${(minutes / 60).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h`;
const fmtPct = (pct: number) => `${pct.toFixed(1)}%`;

function utilizationColor(rate: number): 'success' | 'warning' | 'error' | 'default' {
    if (rate >= 70) return 'success';
    if (rate >= 40) return 'warning';
    if (rate > 0)   return 'error';
    return 'default';
}

/** Sum a numeric field across all periods of an amenity */
function sumField(amenity: AmenityUtilization, field: keyof PeriodData): number {
    return Object.values(amenity.periods).reduce((s, p) => s + (p[field] as number), 0);
}

// ---- Chart builders -------------------------------------------------------

const CHART_COLORS = {
    amenity:     '#2196F3',
    coaching:    '#4CAF50',
    groupPlay:   '#FF9800',
    maintenance: '#F44336',
    holds:       '#9C27B0',
    free:        '#E0E0E0',
};

/**
 * Distributes the capped overall-utilisation percentage proportionally across
 * N booking sources, so the stacked bar always sums to exactly 100 %.
 *
 * sourceMinutes order: [amenity, coaching, groupPlay, maintenance, holds]
 */
function splitProportionally(
    availMins: number,
    sourceMinutes: number[],
): { pcts: number[]; freePct: number } {
    const totalBooked = sourceMinutes.reduce((s, m) => s + m, 0);
    const overallUtil = availMins > 0 ? Math.min(100, (totalBooked / availMins) * 100) : 0;
    if (totalBooked === 0 || overallUtil === 0) {
        return { pcts: sourceMinutes.map(() => 0), freePct: 100 };
    }
    const scale = overallUtil / totalBooked;
    return {
        pcts:    sourceMinutes.map(m => m * scale),
        freePct: Math.max(0, 100 - overallUtil),
    };
}

interface ChartBuildResult {
    chartData: { labels: string[]; datasets: { label: string; data: number[]; backgroundColor: string }[] };
    /** rawHoursPerDataset[datasetIndex][barIndex] = actual hours for that segment */
    rawHoursPerDataset: number[][];
}

function buildChartDataByPeriod(data: CourtUtilizationData): ChartBuildResult {
    const { periods, periodKeys, amenities } = data;

    const rawAmenity:     number[] = [];
    const rawCoaching:    number[] = [];
    const rawGp:          number[] = [];
    const rawMaintenance: number[] = [];
    const rawHolds:       number[] = [];
    const rawFree:        number[] = [];

    const slices = periodKeys.map(key => {
        const avail       = amenities.reduce((s, a) => s + (a.periods[key]?.availableMinutes      ?? 0), 0);
        const booked      = amenities.reduce((s, a) => s + (a.periods[key]?.totalBookedMinutes    ?? 0), 0);
        const amenity     = amenities.reduce((s, a) => s + (a.periods[key]?.amenityBookingMinutes ?? 0), 0);
        const coach       = amenities.reduce((s, a) => s + (a.periods[key]?.coachingMinutes       ?? 0), 0);
        const gp          = amenities.reduce((s, a) => s + (a.periods[key]?.groupPlayMinutes      ?? 0), 0);
        const maintenance = amenities.reduce((s, a) => s + (a.periods[key]?.maintenanceMinutes    ?? 0), 0);
        const holds       = amenities.reduce((s, a) => s + (a.periods[key]?.holdsMinutes          ?? 0), 0);
        rawAmenity.push(amenity / 60);
        rawCoaching.push(coach / 60);
        rawGp.push(gp / 60);
        rawMaintenance.push(maintenance / 60);
        rawHolds.push(holds / 60);
        rawFree.push(Math.max(0, avail - booked) / 60);
        return splitProportionally(avail, [amenity, coach, gp, maintenance, holds]);
    });

    return {
        chartData: {
            labels: periods,
            datasets: [
                { label: 'Court Bookings',   data: slices.map(s => s.pcts[0]), backgroundColor: CHART_COLORS.amenity },
                { label: 'Coaching',         data: slices.map(s => s.pcts[1]), backgroundColor: CHART_COLORS.coaching },
                { label: 'Group Play',       data: slices.map(s => s.pcts[2]), backgroundColor: CHART_COLORS.groupPlay },
                { label: 'Maintenance',      data: slices.map(s => s.pcts[3]), backgroundColor: CHART_COLORS.maintenance },
                { label: 'Holds',            data: slices.map(s => s.pcts[4]), backgroundColor: CHART_COLORS.holds },
                { label: 'Available (free)', data: slices.map(s => s.freePct), backgroundColor: CHART_COLORS.free },
            ],
        },
        rawHoursPerDataset: [rawAmenity, rawCoaching, rawGp, rawMaintenance, rawHolds, rawFree],
    };
}

function buildChartDataByAmenityType(data: CourtUtilizationData): ChartBuildResult {
    const { amenities } = data;
    const types = [...new Set(amenities.map(a => a.type))].sort();

    const rawAmenity:     number[] = [];
    const rawCoaching:    number[] = [];
    const rawGp:          number[] = [];
    const rawMaintenance: number[] = [];
    const rawHolds:       number[] = [];
    const rawFree:        number[] = [];

    const slices = types.map(type => {
        const group       = amenities.filter(a => a.type === type);
        const avail       = group.reduce((s, a) => s + a.totalAvailableMinutes, 0);
        const booked      = group.reduce((s, a) => s + a.totalBookedMinutes,    0);
        const amenity     = group.reduce((s, a) => s + sumField(a, 'amenityBookingMinutes'), 0);
        const coach       = group.reduce((s, a) => s + sumField(a, 'coachingMinutes'),       0);
        const gp          = group.reduce((s, a) => s + sumField(a, 'groupPlayMinutes'),      0);
        const maintenance = group.reduce((s, a) => s + sumField(a, 'maintenanceMinutes'),    0);
        const holds       = group.reduce((s, a) => s + sumField(a, 'holdsMinutes'),          0);
        rawAmenity.push(amenity / 60);
        rawCoaching.push(coach / 60);
        rawGp.push(gp / 60);
        rawMaintenance.push(maintenance / 60);
        rawHolds.push(holds / 60);
        rawFree.push(Math.max(0, avail - booked) / 60);
        return splitProportionally(avail, [amenity, coach, gp, maintenance, holds]);
    });

    return {
        chartData: {
            labels: types,
            datasets: [
                { label: 'Court Bookings',   data: slices.map(s => s.pcts[0]), backgroundColor: CHART_COLORS.amenity },
                { label: 'Coaching',         data: slices.map(s => s.pcts[1]), backgroundColor: CHART_COLORS.coaching },
                { label: 'Group Play',       data: slices.map(s => s.pcts[2]), backgroundColor: CHART_COLORS.groupPlay },
                { label: 'Maintenance',      data: slices.map(s => s.pcts[3]), backgroundColor: CHART_COLORS.maintenance },
                { label: 'Holds',            data: slices.map(s => s.pcts[4]), backgroundColor: CHART_COLORS.holds },
                { label: 'Available (free)', data: slices.map(s => s.freePct), backgroundColor: CHART_COLORS.free },
            ],
        },
        rawHoursPerDataset: [rawAmenity, rawCoaching, rawGp, rawMaintenance, rawHolds, rawFree],
    };
}

function buildChartOptions(rawHoursPerDataset: number[][]) {
    return {
        scales: {
            x: { stacked: true },
            y: {
                stacked: true,
                min: 0,
                max: 100,
                title: { display: true, text: 'Utilization (%)' },
                ticks: {
                    callback: (val: any) => `${val}%`,
                },
            },
        },
        plugins: {
            legend: { position: 'top' as const },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const pct = (ctx.raw as number).toFixed(1);
                        const hrs = rawHoursPerDataset[ctx.datasetIndex]?.[ctx.dataIndex] ?? 0;
                        const hrsFormatted = hrs.toLocaleString('en-US', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                        });
                        return `${ctx.dataset.label}: ${pct}% (${hrsFormatted}h)`;
                    },
                },
            },
        },
    };
}

// ---- Component ------------------------------------------------------------

export const CourtUtilizationReport: React.FC = () => {
    const now = new Date();

    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState<string | null>(null);
    const [reportData, setReportData] = useState<CourtUtilizationData | null>(null);

    // Filters
    const [startDate, setStartDate]         = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate]             = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const [groupBy, setGroupBy]             = useState<'day' | 'week' | 'month'>('day');
    const [amenityType, setAmenityType]     = useState<string>('all');
    const [bookingSource, setBookingSource] = useState<'all' | 'amenity' | 'coaching' | 'group_play'>('all');

    // Chart view toggle — does not trigger a re-fetch, purely frontend
    const [chartView, setChartView] = useState<'period' | 'amenityType'>('period');

    useEffect(() => {
        loadReport();
    }, [startDate, endDate, groupBy, amenityType, bookingSource]);

    const loadReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await reportAPI.getCourtUtilizationReport({
                startDate:     formatDateForInput(startDate),
                endDate:       formatDateForInput(endDate),
                groupBy,
                amenityType:   amenityType === 'all' ? undefined : amenityType,
                bookingSource,
            });
            setReportData(data);
        } catch (err) {
            console.error('Error loading court utilization report:', err);
            setError('Failed to load court utilization report');
        } finally {
            setLoading(false);
        }
    };

    // ---- Render -----------------------------------------------------------

    const { summary } = reportData ?? {};

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
                    to="/reports"
                >
                    Reports
                </RouterLink>
                <Typography color="text.primary">Court Utilization</Typography>
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box>
                    {/* Header + filters */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
                        <Typography variant="h4">Court Utilization Report</Typography>

                        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={v => v && setStartDate(v)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={v => v && setEndDate(v)}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                            />
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Group By</InputLabel>
                                <Select
                                    label="Group By"
                                    value={groupBy}
                                    onChange={e => setGroupBy(e.target.value as any)}
                                >
                                    <MenuItem value="day">Day</MenuItem>
                                    <MenuItem value="week">Week</MenuItem>
                                    <MenuItem value="month">Month</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Amenity Type</InputLabel>
                                <Select
                                    label="Amenity Type"
                                    value={amenityType}
                                    onChange={e => setAmenityType(e.target.value)}
                                >
                                    <MenuItem value="all">All Types</MenuItem>
                                    {(reportData?.amenityTypes ?? []).map(t => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                                <InputLabel>Booking Source</InputLabel>
                                <Select
                                    label="Booking Source"
                                    value={bookingSource}
                                    onChange={e => setBookingSource(e.target.value as any)}
                                >
                                    <MenuItem value="all">All Sources</MenuItem>
                                    <MenuItem value="amenity">Court Bookings</MenuItem>
                                    <MenuItem value="coaching">Coaching</MenuItem>
                                    <MenuItem value="group_play">Group Play</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Loading / error */}
                    {loading && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {!loading && reportData && summary && (
                        <>
                            {/* Summary cards — only Utilization Rate + Booking Breakdown */}
                            <Grid container spacing={3} sx={{ mb: 4 }} alignItems="stretch">
                                {/* Utilization Rate */}
                                <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ display: 'flex' }}>
                                    <Card elevation={2} sx={{ width: '100%' }}>
                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <BarChartIcon sx={{ mr: 1, color: 'warning.main' }} />
                                                <Typography color="textSecondary" variant="body2">Overall Utilization Rate</Typography>
                                            </Box>
                                            <Typography
                                                variant="h4"
                                                fontWeight="bold"
                                                color={
                                                    summary.overallUtilizationRate >= 70
                                                        ? 'success.main'
                                                        : summary.overallUtilizationRate >= 40
                                                        ? 'warning.main'
                                                        : 'text.primary'
                                                }
                                            >
                                                {fmtPct(summary.overallUtilizationRate)}
                                            </Typography>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(100, summary.overallUtilizationRate)}
                                                color={
                                                    summary.overallUtilizationRate >= 70
                                                        ? 'success'
                                                        : summary.overallUtilizationRate >= 40
                                                        ? 'warning'
                                                        : 'error'
                                                }
                                                sx={{ mt: 1, mb: 1.5, borderRadius: 1 }}
                                            />
                                            <Box display="flex" justifyContent="space-between" mt="auto">
                                                <Box>
                                                    <Typography variant="caption" color="textSecondary">Available</Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(summary.totalAvailableHours)}
                                                    </Typography>
                                                </Box>
                                                <Box textAlign="center">
                                                    <Typography variant="caption" color="textSecondary">Booked</Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(summary.totalBookedHours)}
                                                    </Typography>
                                                </Box>
                                                <Box textAlign="right">
                                                    <Typography variant="caption" color="textSecondary">Free</Typography>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(Math.max(0, summary.totalAvailableHours - summary.totalBookedHours))}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Booking Breakdown */}
                                <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ display: 'flex' }}>
                                    <Card elevation={2} sx={{ width: '100%' }}>
                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                                                <Typography color="textSecondary" variant="body2">Booking Breakdown</Typography>
                                            </Box>
                                            <Box display="flex" flexDirection="column" justifyContent="space-evenly" flexGrow={1}>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box display="flex" alignItems="center" gap={0.75}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CHART_COLORS.amenity }} />
                                                        <Typography variant="body2" color="textSecondary">Court Bookings</Typography>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(summary.amenityBookingHours)}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box display="flex" alignItems="center" gap={0.75}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CHART_COLORS.coaching }} />
                                                        <Typography variant="body2" color="textSecondary">Coaching</Typography>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(summary.coachingHours)}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box display="flex" alignItems="center" gap={0.75}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CHART_COLORS.groupPlay }} />
                                                        <Typography variant="body2" color="textSecondary">Group Play</Typography>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(summary.groupPlayHours)}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box display="flex" alignItems="center" gap={0.75}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CHART_COLORS.maintenance }} />
                                                        <Typography variant="body2" color="textSecondary">Maintenance</Typography>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(summary.maintenanceHours ?? 0)}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                                    <Box display="flex" alignItems="center" gap={0.75}>
                                                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: CHART_COLORS.holds }} />
                                                        <Typography variant="body2" color="textSecondary">Holds</Typography>
                                                    </Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {fmtHours(summary.holdsHours ?? 0)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Stacked bar chart — percentage y-axis */}
                            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">
                                        Court Utilization by{' '}
                                        {chartView === 'period' ? 'Period' : 'Amenity Type'}
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={chartView}
                                        exclusive
                                        onChange={(_, v) => { if (v) setChartView(v); }}
                                        size="small"
                                    >
                                        <ToggleButton value="period">
                                            <CalendarToday fontSize="small" sx={{ mr: 0.5 }} />
                                            By Period
                                        </ToggleButton>
                                        <ToggleButton value="amenityType">
                                            <Category fontSize="small" sx={{ mr: 0.5 }} />
                                            By Amenity Type
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>

                                {reportData.amenities.length === 0 ? (
                                    <Typography color="textSecondary">
                                        No amenity data available for the selected filters.
                                    </Typography>
                                ) : (() => {
                                    const { chartData, rawHoursPerDataset } =
                                        chartView === 'period'
                                            ? buildChartDataByPeriod(reportData)
                                            : buildChartDataByAmenityType(reportData);
                                    return (
                                        <ReportChart
                                            type="bar"
                                            data={chartData}
                                            height={350}
                                            options={buildChartOptions(rawHoursPerDataset)}
                                        />
                                    );
                                })()}
                            </Paper>

                            {/* Per-amenity table */}
                            <Paper elevation={2} sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Per-Amenity Breakdown
                                </Typography>
                                {reportData.amenities.length === 0 ? (
                                    <Typography color="textSecondary">No amenity data available.</Typography>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Amenity</strong></TableCell>
                                                    <TableCell><strong>Type</strong></TableCell>
                                                    <TableCell align="right"><strong>Available</strong></TableCell>
                                                    <TableCell align="right"><strong>Court Bookings</strong></TableCell>
                                                    <TableCell align="right"><strong>Coaching</strong></TableCell>
                                                    <TableCell align="right"><strong>Group Play</strong></TableCell>
                                                    <TableCell align="right"><strong>Maintenance</strong></TableCell>
                                                    <TableCell align="right"><strong>Holds</strong></TableCell>
                                                    <TableCell align="right"><strong>Total Booked</strong></TableCell>
                                                    <TableCell align="right"><strong>Utilization</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {[...reportData.amenities]
                                                    .sort((a, b) => a.name.localeCompare(b.name))
                                                    .map(amenity => {
                                                    const amenityBookingMin = sumField(amenity, 'amenityBookingMinutes');
                                                    const coachingMin       = sumField(amenity, 'coachingMinutes');
                                                    const gpMin             = sumField(amenity, 'groupPlayMinutes');
                                                    const maintenanceMin    = sumField(amenity, 'maintenanceMinutes');
                                                    const holdsMin          = sumField(amenity, 'holdsMinutes');

                                                    return (
                                                        <TableRow key={amenity.id} hover>
                                                            <TableCell>{amenity.name}</TableCell>
                                                            <TableCell>
                                                                <Chip label={amenity.type} size="small" variant="outlined" />
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {fmtMinH(amenity.totalAvailableMinutes)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography
                                                                    variant="body2"
                                                                    color={amenityBookingMin > 0 ? CHART_COLORS.amenity : 'text.secondary'}
                                                                    fontWeight={amenityBookingMin > 0 ? 'medium' : 'normal'}
                                                                >
                                                                    {fmtMinH(amenityBookingMin)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography
                                                                    variant="body2"
                                                                    color={coachingMin > 0 ? CHART_COLORS.coaching : 'text.secondary'}
                                                                    fontWeight={coachingMin > 0 ? 'medium' : 'normal'}
                                                                >
                                                                    {fmtMinH(coachingMin)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography
                                                                    variant="body2"
                                                                    color={gpMin > 0 ? CHART_COLORS.groupPlay : 'text.secondary'}
                                                                    fontWeight={gpMin > 0 ? 'medium' : 'normal'}
                                                                >
                                                                    {fmtMinH(gpMin)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography
                                                                    variant="body2"
                                                                    color={maintenanceMin > 0 ? CHART_COLORS.maintenance : 'text.secondary'}
                                                                    fontWeight={maintenanceMin > 0 ? 'medium' : 'normal'}
                                                                >
                                                                    {fmtMinH(maintenanceMin)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Typography
                                                                    variant="body2"
                                                                    color={holdsMin > 0 ? CHART_COLORS.holds : 'text.secondary'}
                                                                    fontWeight={holdsMin > 0 ? 'medium' : 'normal'}
                                                                >
                                                                    {fmtMinH(holdsMin)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {fmtMinH(amenity.totalBookedMinutes)}
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                <Tooltip
                                                                    title={`${fmtMinH(amenity.totalBookedMinutes)} booked of ${fmtMinH(amenity.totalAvailableMinutes)} available`}
                                                                >
                                                                    <Box>
                                                                        <Chip
                                                                            label={fmtPct(amenity.utilizationRate)}
                                                                            size="small"
                                                                            color={utilizationColor(amenity.utilizationRate)}
                                                                            variant={amenity.utilizationRate > 0 ? 'filled' : 'outlined'}
                                                                        />
                                                                    </Box>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                                {/* Totals row */}
                                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                    <TableCell colSpan={2}><strong>Total</strong></TableCell>
                                                    <TableCell align="right">
                                                        <strong>{fmtHours(summary.totalAvailableHours)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>{fmtHours(summary.amenityBookingHours)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>{fmtHours(summary.coachingHours)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>{fmtHours(summary.groupPlayHours)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>{fmtHours(summary.maintenanceHours ?? 0)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>{fmtHours(summary.holdsHours ?? 0)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <strong>{fmtHours(summary.totalBookedHours)}</strong>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Chip
                                                            label={fmtPct(summary.overallUtilizationRate)}
                                                            size="small"
                                                            color={utilizationColor(summary.overallUtilizationRate)}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </>
                    )}

                    {!loading && !error && !reportData && (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                            <Typography color="textSecondary">Select a date range to load the report.</Typography>
                        </Box>
                    )}
                </Box>
            </LocalizationProvider>
        </Container>
    );
};

export default CourtUtilizationReport;
